import React, {useState} from 'react';
import {observer} from 'mobx-react';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {IconButton, InputAdornment} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import classNames from 'classnames';
import styles from './Input.module.scss';
import {ThemeProvider} from '@mui/material/styles';
import {theme} from '../../helpers/theme';
import dayjs, {Dayjs} from 'dayjs';
import InputDate from './InputDate';

export interface InputPropsCommon {
    value: string;
    onChange: (value: string) => void;
    onKeyDown: (value: React.SyntheticEvent<EventTarget>) => void;
    placeholder?: string;
    label?: string;
    disabled?: boolean;
    error?: string;
    ariaLabel?: string | 'unlabeled';
    onBlur?: () => void;
    type?: 'email' | 'text' | 'tel';
}

interface InputDateWithPickerProps extends InputPropsCommon {
    minDate?: Dayjs;
    maxDate?: Dayjs;
    className?: string;
}

const DATE_FORMAT = 'MM/DD/YYYY';

const InputDateWithPicker: React.FC<InputDateWithPickerProps> = (props) => {
    const {
        value,
        onChange,
        onKeyDown,
        label,
        placeholder,
        disabled,
        error,
        ariaLabel,
        onBlur,
        minDate,
        maxDate,
        className,
    } = props;

    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const getDateValue = (): Dayjs | null => {
        if (!value) return null;
        try {
            const parsedDate = dayjs(value);
            return parsedDate.isValid() ? parsedDate : null;
        } catch {
            return null;
        }
    };

    const handleDatePickerChange = (date: Dayjs | null) => {
        if (date && date.isValid()) {
            const formattedDate = date.format(DATE_FORMAT);
            onChange(formattedDate);
        } else {
            onChange('');
        }
        setIsPickerOpen(false);
    };

    return (
        <div
            className={classNames(styles.Input, styles.inputText, className, {
                [styles.error]: !!error,
            })}
        >
            <ThemeProvider theme={theme}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <InputDate
                        value={value}
                        onChange={onChange}
                        onKeyDown={onKeyDown}
                        onBlur={onBlur}
                        ariaLabel={ariaLabel}
                        label={label}
                        placeholder={placeholder}
                        error={error}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={(event) => {
                                            setIsPickerOpen(true);
                                            setAnchorEl(event.currentTarget);
                                        }}
                                        edge="end"
                                        disabled={disabled}
                                        aria-label="open date picker"
                                        color="secondary"
                                    >
                                        <CalendarTodayIcon sx={{width: 20, height: 20}} />
                                    </IconButton>
                                </InputAdornment>
                            ),
                            sx: {pr: {md: 3}},
                        }}
                    />

                    <DatePicker
                        open={isPickerOpen}
                        value={getDateValue()}
                        onChange={handleDatePickerChange}
                        onAccept={onBlur}
                        minDate={minDate}
                        maxDate={maxDate}
                        disabled={disabled}
                        renderInput={() => <div />}
                        onClose={() => setIsPickerOpen(false)}
                        PopperProps={{
                            placement: 'bottom-end',
                            anchorEl: anchorEl,
                        }}
                        DialogProps={{
                            sx: {
                                '& .MuiPickersToolbar-penIconButton': {
                                    display: {xs: 'none', md: 'inline-flex'},
                                },
                            },
                        }}
                    />
                </LocalizationProvider>
            </ThemeProvider>
        </div>
    );
};

export default observer(InputDateWithPicker);
